/** Pure ticket prefill, eligibility and count contract; no Help catalogue loading. */
import {
  HELP_CHAT_RATE_LIMIT_REPLY,
  HELP_CHAT_TRANSPORT_ERROR_REPLY,
  SENSITIVE_HELP_CHAT_ERROR,
} from './help-chat-guard';
import { normalizeHelpChatPath } from './help-path';
import { HELP_TICKET_CATEGORY_LABELS } from './ticket-labels';

export const STATURA_HOW_TO_CONTINUE_REQUIRED = 'HOW_TO_HELP_REQUIRED' as const;

const HELP_QUESTION_PREFIX = 'Help question:';
const GUIDES_TRIED_PREFIX = 'Guides tried:';
const SCREEN_PREFIX = 'Screen:';
const CATEGORY_PREFIX = 'Category:';
const CONTINUED_PREFIX = 'Continued after help:';
const COUNT_LINE_PREFIXES = [
  HELP_QUESTION_PREFIX,
  GUIDES_TRIED_PREFIX,
  SCREEN_PREFIX,
  CATEGORY_PREFIX,
  CONTINUED_PREFIX,
] as const;

export type SupportTicketDeflectionInput = {
  tier: string;
  category: string;
  continuedAfterHelp: boolean;
};

export function isStaturaHowToTicket(tier: string, category: string): boolean {
  return tier === 'STATURA' && category === 'HOW_TO';
}

export function canSubmitSupportTicket(
  input: SupportTicketDeflectionInput,
): { ok: true } | { ok: false; code: typeof STATURA_HOW_TO_CONTINUE_REQUIRED } {
  if (isStaturaHowToTicket(input.tier, input.category) && !input.continuedAfterHelp) {
    return { ok: false, code: STATURA_HOW_TO_CONTINUE_REQUIRED };
  }
  return { ok: true };
}

export function isHelpChatTicketEligible(input: {
  content: string;
  resultCount?: number;
}): boolean {
  if (input.content === SENSITIVE_HELP_CHAT_ERROR) return false;
  if (input.content === HELP_CHAT_RATE_LIMIT_REPLY) return false;
  if (input.content === HELP_CHAT_TRANSPORT_ERROR_REPLY) return false;
  if (input.content.includes('This conversation has reached its limit')) return false;
  return typeof input.resultCount === 'number';
}

function sanitiseArticleIds(ids: readonly string[] | undefined): string[] {
  if (!ids) return [];
  const seen = new Set<string>();
  const clean: string[] = [];
  for (const raw of ids) {
    const id = raw.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{1,80}$/.test(id) || seen.has(id)) continue;
    seen.add(id);
    clean.push(id);
    if (clean.length >= 6) break;
  }
  return clean;
}

export function buildHelpTicketDescription(input: {
  question?: string;
  articleIds?: readonly string[];
  path?: string | null;
  category?: string;
  continuedAfterHelp?: boolean;
}): string {
  const lines: string[] = [];
  const question = input.question?.trim().slice(0, 180);
  if (question) lines.push(`${HELP_QUESTION_PREFIX} ${question}`);
  const articleIds = sanitiseArticleIds(input.articleIds);
  if (articleIds.length > 0) lines.push(`${GUIDES_TRIED_PREFIX} ${articleIds.join(', ')}`);
  const path = normalizeHelpChatPath(input.path);
  if (path) lines.push(`${SCREEN_PREFIX} ${path}`);
  const category = input.category?.trim().toUpperCase();
  if (category && category in HELP_TICKET_CATEGORY_LABELS) {
    lines.push(`${CATEGORY_PREFIX} ${category}`);
  }
  if (input.continuedAfterHelp) lines.push(`${CONTINUED_PREFIX} yes`);
  return lines.join('\n');
}

export function parseHelpTicketCountFields(description: string | null | undefined): {
  question: string | null;
  articleIds: string[];
  path: string | null;
  category: string | null;
  continuedAfterHelp: boolean;
} {
  const empty = {
    question: null as string | null,
    articleIds: [] as string[],
    path: null as string | null,
    category: null as string | null,
    continuedAfterHelp: false,
  };
  if (!description) return empty;

  let question: string | null = null;
  let articleIds: string[] = [];
  let path: string | null = null;
  let category: string | null = null;
  let continuedAfterHelp = false;

  for (const rawLine of description.split('\n')) {
    const line = rawLine.trim();
    if (line.startsWith(HELP_QUESTION_PREFIX)) {
      question = line.slice(HELP_QUESTION_PREFIX.length).trim().slice(0, 180) || null;
    } else if (line.startsWith(GUIDES_TRIED_PREFIX)) {
      articleIds = sanitiseArticleIds(
        line
          .slice(GUIDES_TRIED_PREFIX.length)
          .split(',')
          .map((part) => part.trim()),
      );
    } else if (line.startsWith(SCREEN_PREFIX)) {
      path = normalizeHelpChatPath(line.slice(SCREEN_PREFIX.length).trim());
    } else if (line.startsWith(CATEGORY_PREFIX)) {
      const value = line.slice(CATEGORY_PREFIX.length).trim().toUpperCase();
      category = value in HELP_TICKET_CATEGORY_LABELS ? value : null;
    } else if (line.startsWith(CONTINUED_PREFIX)) {
      continuedAfterHelp = line.slice(CONTINUED_PREFIX.length).trim().toLowerCase() === 'yes';
    }
  }

  return { question, articleIds, path, category, continuedAfterHelp };
}

export type HelpTicketCountSummary = {
  total: number;
  continuedAfterHelp: number;
  byCategory: Record<string, number>;
  byPath: Record<string, number>;
};

export function summariseHelpTicketCounts(
  tickets: ReadonlyArray<{ category?: string | null; description?: string | null }>,
): HelpTicketCountSummary {
  const byCategory: Record<string, number> = {};
  const byPath: Record<string, number> = {};
  let continuedAfterHelp = 0;

  for (const ticket of tickets) {
    const fields = parseHelpTicketCountFields(ticket.description);
    if (fields.continuedAfterHelp) continuedAfterHelp += 1;
    const category = fields.category || ticket.category || null;
    if (category) byCategory[category] = (byCategory[category] ?? 0) + 1;
    if (fields.path) byPath[fields.path] = (byPath[fields.path] ?? 0) + 1;
  }

  return { total: tickets.length, continuedAfterHelp, byCategory, byPath };
}

export function mergeHelpTicketCountFields(
  userText: string,
  fields: {
    question?: string;
    articleIds?: readonly string[];
    path?: string | null;
    category?: string;
    continuedAfterHelp?: boolean;
  },
): string {
  const userBody = userText
    .split('\n')
    .filter((line) => !COUNT_LINE_PREFIXES.some((prefix) => line.trim().startsWith(prefix)))
    .join('\n')
    .trim();
  const footer = buildHelpTicketDescription(fields);
  return [userBody, footer].filter((part) => part.length > 0).join('\n\n');
}

export function buildHelpTicketHref(input: {
  category: string;
  subject?: string;
  articleIds?: readonly string[];
  path?: string | null;
  tier?: 'STATURA' | 'INTERNAL';
  continuedAfterHelp?: boolean;
}): string {
  const params = new URLSearchParams();
  params.set('tier', input.tier ?? 'STATURA');
  params.set('category', input.category);
  if (input.subject?.trim()) {
    params.set('subject', input.subject.trim().slice(0, 180));
  }
  const articleIds = sanitiseArticleIds(input.articleIds);
  if (articleIds.length > 0) {
    params.set('articles', articleIds.join(','));
  }
  const path = normalizeHelpChatPath(input.path);
  if (path) {
    params.set('path', path);
  }
  if (input.continuedAfterHelp) {
    params.set('continued', '1');
  }
  const description = buildHelpTicketDescription({
    question: input.subject,
    articleIds,
    path,
    category: input.category,
    continuedAfterHelp: input.continuedAfterHelp,
  });
  if (description) {
    params.set('description', description.slice(0, 500));
  }
  return `/help/tickets/new?${params.toString()}`;
}

export type HelpTicketSearchParams = {
  get(name: string): string | null;
};

export type HelpTicketPrefill = {
  tier: '' | 'INTERNAL' | 'STATURA';
  category: string;
  subject: string;
  description: string;
  articleIds: string[];
  path: string | null;
  continuedAfterHelp: boolean;
  startStep: 1 | 2 | 3;
};

export function parseHelpTicketPrefill(params: HelpTicketSearchParams): HelpTicketPrefill {
  const tierRaw = params.get('tier')?.trim().toUpperCase() ?? '';
  const tier = tierRaw === 'INTERNAL' || tierRaw === 'STATURA' ? tierRaw : '';
  const categoryRaw = params.get('category')?.trim().toUpperCase() ?? '';
  const category = categoryRaw in HELP_TICKET_CATEGORY_LABELS ? categoryRaw : '';
  const subject = (params.get('subject') ?? '').trim().slice(0, 180);
  const articleIds = sanitiseArticleIds((params.get('articles') ?? '').split(','));
  const path = normalizeHelpChatPath(params.get('path'));
  const continuedAfterHelp = params.get('continued') === '1';
  const rebuilt = buildHelpTicketDescription({
    question: subject,
    articleIds,
    path,
    category,
    continuedAfterHelp,
  });
  const descriptionFromQuery = (params.get('description') ?? '').trim().slice(0, 500);
  const description = rebuilt || descriptionFromQuery;

  let startStep: 1 | 2 | 3 = 1;
  if (tier && category && subject) startStep = 3;
  else if (tier && category) startStep = 2;

  return {
    tier,
    category,
    subject,
    description,
    articleIds,
    path,
    continuedAfterHelp,
    startStep,
  };
}
